/**
 * Reading and writing the user's own data.
 *
 * Everything here goes through the browser's Supabase client, so row-level
 * security is what enforces "your list is yours" — there is no server-side
 * ownership check to forget. Search and add go via the worker instead,
 * because those need API tokens that must not reach the browser.
 */

import type { Details } from '../details/types.js';
import type { DateSlot, Snapshot } from '../poller/types.js';
import type { SiteCursor } from '../site/types.js';
import type { DescriptionSource, WatchEvent, WatchItem } from './lib/model.js';
import { supabase } from './supabase.ts';

interface SubscriptionRow {
  id: string;
  consumed_at: string | null;
  show_placeholder: boolean;
  event_types: string[] | null;
  formats: string[] | null;
  region: string | null;
  site_seen_at: string | null;
  watchables: Record<string, unknown> | null;
}

function toItem(row: SubscriptionRow): WatchItem | null {
  const w = row.watchables;
  if (!w) return null;

  const snapshot = w.snapshot as Snapshot | null;
  return {
    subscriptionId: row.id,
    watchableId: String(w.id),
    source: w.source as WatchItem['source'],
    sourceId: String(w.source_id),
    kind: w.kind as WatchItem['kind'],
    medium: w.medium as WatchItem['medium'],
    title: String(w.title ?? 'Untitled'),
    byline: (w.byline as string | null) ?? null,
    imageUrl: (w.image_url as string | null) ?? null,
    description: (w.description as string | null) ?? null,
    descriptionSource: ((w.description_source as DescriptionSource) ?? 'own'),
    tags: Array.isArray(w.tags) ? (w.tags as string[]) : [],
    rating: w.rating == null ? null : Number(w.rating),
    // An empty jsonb default means "never polled", which is not the same as
    // "polled and found nothing" — the UI must not read {} as a snapshot.
    snapshot: snapshot && Object.keys(snapshot).length > 0 ? snapshot : null,
    // Same rule as the snapshot: an empty jsonb default means "never
    // described", which the screens must not read as "nothing to say".
    details: hasKeys(w.details) ? (w.details as Details) : null,
    completion: (w.completion as WatchItem['completion']) ?? null,
    knownPositions: w.known_positions == null ? null : Number(w.known_positions),
    expectedTotal: w.expected_total == null ? null : Number(w.expected_total),
    expectedTotalNote: (w.expected_total_note as string | null) ?? null,
    medianGapDays: w.median_gap_days == null ? null : Number(w.median_gap_days),
    consumedAt: row.consumed_at,
    showPlaceholder: row.show_placeholder !== false,
    siteUrl: (w.site_url as string | null) ?? null,
    // Same rule as snapshot and details: `{}` is "never looked", and the
    // marker must not treat a first look as a change.
    siteCursor: hasKeys(w.site_cursor) ? (w.site_cursor as SiteCursor) : null,
    siteSeenAt: row.site_seen_at ?? null,
    // Null and empty mean the same thing here — every format — so both
    // normalise to the empty array rather than leaving the screens to guess.
    formats: Array.isArray(row.formats) ? (row.formats as DateSlot[]) : [],
    region: row.region ?? null,
  };
}

const WATCHABLE_COLUMNS = `
  id, source, source_id, kind, medium, title, byline, image_url,
  description, description_source, tags, rating, snapshot, details,
  completion, known_positions, expected_total, expected_total_note, median_gap_days,
  site_url, site_cursor
`;

function hasKeys(value: unknown): boolean {
  return Boolean(value) && typeof value === 'object' && Object.keys(value as object).length > 0;
}

export async function loadWatchlist(): Promise<WatchItem[]> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(
      `id, consumed_at, show_placeholder, event_types, formats, region, site_seen_at,
       watchables (${WATCHABLE_COLUMNS})`,
    );

  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as SubscriptionRow[])
    .map(toItem)
    .filter((i): i is WatchItem => i !== null);
}

export async function loadEvents(limit = 100): Promise<WatchEvent[]> {
  // RLS on `events` allows any signed-in user to read the shared catalogue,
  // so this is scoped to the caller's own subscriptions explicitly.
  const { data, error } = await supabase
    .from('events')
    .select('id, watchable_id, type, payload, detected_at, event_reads(user_id)')
    .order('detected_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(error.message);

  return ((data ?? []) as unknown as Array<Record<string, unknown>>).map((row) => ({
    id: String(row.id),
    watchableId: String(row.watchable_id),
    type: row.type as WatchEvent['type'],
    payload: (row.payload ?? {}) as Record<string, unknown>,
    detectedAt: String(row.detected_at),
    // Absent row means unseen, so nothing needs backfilling when someone
    // adds a watchable that already has history.
    seen: Array.isArray(row.event_reads) && row.event_reads.length > 0,
  }));
}

export async function markSeen(eventIds: readonly string[], userId: string): Promise<void> {
  if (eventIds.length === 0) return;
  const { error } = await supabase
    .from('event_reads')
    .upsert(eventIds.map((id) => ({ user_id: userId, event_id: id })), {
      onConflict: 'user_id,event_id',
    });
  if (error) throw new Error(error.message);
}

export async function touchLastSeen(userId: string): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ last_seen_at: new Date().toISOString() })
    .eq('id', userId);
  if (error) throw new Error(error.message);
}

export async function loadLastSeen(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('last_seen_at')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data?.last_seen_at as string | undefined) ?? null;
}

/**
 * Whether slate emails this person when something becomes available.
 *
 * On for a new account: the only way in is a magic link, so every account
 * arrives with a confirmed address, and an app whose whole job is telling you
 * a thing came out would be odd to make you ask for it twice. The switch in
 * Settings and the link in every message are the two ways off.
 *
 * Only `released` is ever mailed — a date moving or firming up stays in the
 * app. The copy beside this switch has to say so, or "email me" reads as a
 * promise to forward the whole changes feed.
 */
export async function loadEmailReleases(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('email_releases')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  // Absent means the migration has not run; on is the default it will get.
  return (data?.email_releases as boolean | undefined) ?? true;
}

export async function setEmailReleases(userId: string, on: boolean): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .update({ email_releases: on })
    .eq('id', userId);
  if (error) throw new Error(error.message);
}

export async function setConsumed(subscriptionId: string, consumed: boolean): Promise<void> {
  const { error } = await supabase
    .from('subscriptions')
    .update({ consumed_at: consumed ? new Date().toISOString() : null })
    .eq('id', subscriptionId);
  if (error) throw new Error(error.message);
}

/**
 * Formats and region are stored on the subscription, not the watchable: two
 * people can follow the same book and want different things from it, and the
 * catalogue row is shared between them.
 */
export async function setPreferences(
  subscriptionId: string,
  preferences: { formats?: string[]; region?: string | null },
): Promise<void> {
  const { error } = await supabase
    .from('subscriptions')
    .update(preferences)
    .eq('id', subscriptionId);
  if (error) throw new Error(error.message);
}

/**
 * "I have looked." Written when the reader opens the site, not when they leave
 * the screen — the claim the marker makes is only that they have not been since
 * it changed, and clicking through settles that.
 */
export async function markSiteSeen(subscriptionId: string): Promise<void> {
  const { error } = await supabase
    .from('subscriptions')
    .update({ site_seen_at: new Date().toISOString() })
    .eq('id', subscriptionId);
  if (error) throw new Error(error.message);
}

export async function setEventTypes(subscriptionId: string, types: string[]): Promise<void> {
  const { error } = await supabase
    .from('subscriptions')
    .update({ event_types: types })
    .eq('id', subscriptionId);
  if (error) throw new Error(error.message);
}

export async function unsubscribe(subscriptionId: string): Promise<void> {
  // Only ever removes *your* subscription. The watchable stays: other people
  // may be following it, and its history is shared catalogue data.
  const { error } = await supabase.from('subscriptions').delete().eq('id', subscriptionId);
  if (error) throw new Error(error.message);
}
